import { isCancelledError } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import type { User, UserTeam } from '@skop-app/lib-domain/types'

import type { SessionRuntime } from '@/services/auth/sessionRuntime'

import { userKeys } from './user.keys'

export const CURRENT_USER_STALE_TIME = 1000 * 60 * 2

export const MEMBERSHIP_SYNC_DELAYS_MS = [400, 1200, 3000] as const

export type CurrentUserQueryParams = Pick<
  SessionRuntime,
  'sessionScopeKey' | 'currentUserId' | 'impersonatedUserId'
>

export function getCurrentUserQueryKeys(
  params: CurrentUserQueryParams,
): ReadonlyArray<readonly unknown[]> {
  if (params.currentUserId) {
    return [userKeys.currentScoped(params)]
  }

  return [userKeys.current()]
}

export function getEffectiveCurrentUserId(
  params: CurrentUserQueryParams,
): string | undefined {
  return params.impersonatedUserId ?? params.currentUserId
}

export async function invalidateCurrentUserQueries(
  queryClient: QueryClient,
  params: CurrentUserQueryParams,
): Promise<void> {
  const results = await Promise.allSettled(
    getCurrentUserQueryKeys(params).map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  )

  const unexpectedError = results.find(
    (result) =>
      result.status === 'rejected' && !isCancelledError(result.reason),
  )

  if (unexpectedError?.status === 'rejected') {
    throw unexpectedError.reason
  }
}

export function upsertCurrentUserOrganizationMembership(
  queryClient: QueryClient,
  params: CurrentUserQueryParams,
  membership: UserTeam,
): void {
  for (const queryKey of getCurrentUserQueryKeys(params)) {
    queryClient.setQueryData<User | null>(queryKey, (previous) => {
      if (!previous) return previous

      const organizations = previous.organizations ?? []
      if (organizations.some((team) => team.value === membership.value)) {
        return previous
      }

      return {
        ...previous,
        organizations: [...organizations, membership].sort((left, right) =>
          (left.label ?? '').localeCompare(right.label ?? ''),
        ),
      }
    })
  }
}

export function currentUserHasOrganizationMembership(
  queryClient: QueryClient,
  params: CurrentUserQueryParams,
  organizationId: string,
): boolean {
  return getCurrentUserQueryKeys(params).some((queryKey) =>
    Boolean(
      queryClient
        .getQueryData<User | null>(queryKey)
        ?.organizations?.some((team) => team.value === organizationId),
    ),
  )
}

const wait = (delayMs: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, delayMs)
  })

export async function reconcileCurrentUserOrganizationMembership(
  queryClient: QueryClient,
  params: CurrentUserQueryParams,
  membership: UserTeam,
  delaysMs: ReadonlyArray<number> = MEMBERSHIP_SYNC_DELAYS_MS,
): Promise<boolean> {
  for (const delayMs of delaysMs) {
    await wait(delayMs)

    const refreshed = await Promise.allSettled(
      getCurrentUserQueryKeys(params).map((queryKey) =>
        queryClient.refetchQueries({ queryKey }, { throwOnError: true }),
      ),
    )
    if (refreshed.some((result) => result.status === 'rejected')) continue

    if (
      currentUserHasOrganizationMembership(
        queryClient,
        params,
        membership.value,
      )
    ) {
      return true
    }

    upsertCurrentUserOrganizationMembership(queryClient, params, membership)
  }

  return false
}
